/**
 * AdminHealthPanel — Tableau de bord "Santé de l'app" post-lancement (16/09/2026).
 * Erreurs critiques récentes (paiements Stripe échoués, générations IA échouées) +
 * usage IA (générations muscu + cardio) cette semaine / ce mois.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Sparkles, CreditCard, ActivitySquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERROR_META = {
  payment_failed: { color: '#f87171', icon: CreditCard },
  ai_workout_failed: { color: '#fbbf24', icon: Sparkles },
  ai_cardio_failed: { color: '#fbbf24', icon: Sparkles },
};

export const AdminHealthPanel = ({ onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/health-dashboard`, { credentials: 'include' });
        if (res.status === 401) { onLogout?.(); return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Erreur chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onLogout]);

  if (loading) {
    return <div className="flex items-center justify-center py-24" data-testid="health-loading"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  }
  if (error) {
    return <div className="text-center text-zinc-400 py-12" data-testid="health-error">{error}</div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-health-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 to-black p-4" data-testid="health-ai-week-card">
          <Sparkles className="w-5 h-5 text-primary mb-2" />
          <div className="text-2xl font-black text-white">{data.ai_generations_week}</div>
          <p className="text-zinc-500 text-xs mt-0.5">Générations IA · 7 derniers jours</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-4" data-testid="health-ai-month-card">
          <ActivitySquare className="w-5 h-5 text-zinc-400 mb-2" />
          <div className="text-2xl font-black text-white">{data.ai_generations_month}</div>
          <p className="text-zinc-500 text-xs mt-0.5">Générations IA · 30 derniers jours</p>
        </div>
        <div className={`rounded-2xl border p-4 ${data.failed_payments_30d > 0 ? 'border-red-500/30 bg-red-500/5' : 'border-white/10 bg-zinc-900/60'}`} data-testid="health-payments-card">
          <CreditCard className={`w-5 h-5 mb-2 ${data.failed_payments_30d > 0 ? 'text-red-400' : 'text-zinc-400'}`} />
          <div className="text-2xl font-black text-white">{data.failed_payments_30d}</div>
          <p className="text-zinc-500 text-xs mt-0.5">Paiements échoués · 30j</p>
        </div>
        <div className={`rounded-2xl border p-4 ${data.ai_failures_30d > 0 ? 'border-amber-500/30 bg-amber-500/5' : 'border-white/10 bg-zinc-900/60'}`} data-testid="health-ai-failures-card">
          <AlertTriangle className={`w-5 h-5 mb-2 ${data.ai_failures_30d > 0 ? 'text-amber-400' : 'text-zinc-400'}`} />
          <div className="text-2xl font-black text-white">{data.ai_failures_30d}</div>
          <p className="text-zinc-500 text-xs mt-0.5">Générations IA échouées · 30j</p>
        </div>
      </div>

      <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 sm:p-5" data-testid="health-errors-card">
        <h3 className="text-white font-semibold inline-flex items-center gap-2 text-sm mb-4">
          <AlertTriangle className="w-4 h-4 text-primary" /> Erreurs critiques récentes (50 dernières)
        </h3>
        {data.errors.length === 0 ? (
          <div className="py-8 text-center text-zinc-500 text-sm" data-testid="health-no-errors">Aucune erreur critique enregistrée. Tout va bien ! 🎉</div>
        ) : (
          <div className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="health-errors-list">
            {data.errors.map((e) => {
              const meta = ERROR_META[e.error_type] || { color: '#a1a1aa', icon: AlertTriangle };
              const Icon = meta.icon;
              return (
                <div key={e.log_id} className="flex items-start gap-2.5 text-xs bg-white/5 rounded-lg px-3 py-2" data-testid={`health-error-${e.log_id}`}>
                  <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: meta.color }} />
                  <div className="min-w-0">
                    <span className="font-semibold text-white">{e.label}</span>
                    {e.email && <span className="text-zinc-500"> · {e.email}</span>}
                    <span className="text-zinc-600"> · {String(e.created_at).slice(0, 16).replace('T', ' ')}</span>
                    {e.detail && <p className="text-zinc-500 mt-0.5 truncate">{e.detail}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHealthPanel;
